/**
 * Pelacakan pengunjung HALAMAN JUALAN PLATFORM (kelolify.com) — helper MURNI.
 *
 * Tanpa React / Supabase / DOM: seluruh keputusan "kunjungan ini dicatat atau
 * tidak, dan atribusinya apa" tinggal di sini supaya bisa diuji
 * (`__tests__/platformVisitor.test.ts`). Komponennya
 * (`PlatformVisitorTracker`) hanya membaca `window` lalu memanggil fungsi di
 * berkas ini.
 *
 * ⛔ JANGAN dikelirukan dengan `VisitorTracker` (pengunjung TOKO, tabel
 * `page_views`). Keduanya SENGAJA terpisah dan tidak boleh disatukan:
 * pengunjung kelolify.com adalah calon PENYEWA, sedangkan pengunjung
 * galeastore.id adalah PEMBELI. Mencampurnya berarti angka "Pengunjung
 * Website" di dasbor admin toko ikut menghitung orang yang tak akan pernah
 * membeli apa pun di toko itu.
 *
 * ATURAN BENTUK (satu-satunya salinan). SQL `record_platform_page_view`
 * (mig 4100) SENGAJA hanya menjaga panjang & kekosongan — kalau aturan bentuk
 * ikut disalin ke SQL, cepat atau lambat dua salinan itu berbeda pendapat
 * tentang satu perujuk yang sama dan angkanya pecah jadi dua baris.
 */

/** localStorage: identitas pengunjung acak (BUKAN identitas orang). */
export const PLATFORM_VISITOR_ID_KEY = "mitrahub-platform-visitor-id";
/** localStorage: hari terakhir kunjungan tercatat, untuk dedup sisi klien. */
export const PLATFORM_VISITED_DAY_KEY = "mitrahub-platform-visited-day";

/** Panjang maksimum — cermin `left(...)` di SQL. */
const MAX_PATH = 200;
const MAX_HOST = 128;
const MAX_UTM = 64;
const MAX_CAMPAIGN = 96;

export type PlatformVisitAttribution = {
  path: string;
  referrerHost: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
};

function clean(raw: string | null | undefined, max: number): string | null {
  const v = (raw ?? "").trim().toLowerCase().slice(0, max);
  return v === "" ? null : v;
}

/**
 * Tanggal LOKAL "YYYY-MM-DD" (zona browser). Dedup sisi klien saja — batas
 * hari yang MENGIKAT tetap WIB di server, karena hanya server yang tahu zona
 * yang benar. Selisih zona paling buruk membuat satu panggilan RPC tambahan
 * yang langsung di-dedup server; ia tak pernah menggandakan angka.
 */
export function localDay(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Path pendaratan yang disimpan.
 *
 * `/platform` DINORMALKAN JADI `/` — dan ini bukan kosmetik. Di apex,
 * middleware me-REWRITE `/` → `/platform`, jadi alamat yang dilihat & dibagikan
 * pengunjung selalu `/`; tapi `/platform` juga bisa dibuka langsung. Tanpa
 * normalisasi ini SATU halaman yang sama akan terbelah jadi dua baris
 * peringkat, dan tak satu pun dari keduanya menunjukkan jumlah sebenarnya.
 */
export function normalizeVisitPath(pathname: string | null | undefined): string {
  const raw = (pathname ?? "").trim();
  if (raw === "" || raw === "/platform" || raw === "/platform/") return "/";
  const noQuery = raw.split(/[?#]/)[0];
  const path = noQuery.startsWith("/") ? noQuery : `/${noQuery}`;
  // Buang garis miring di ekor supaya `/harga` & `/harga/` tak jadi dua baris.
  const trimmed = path.length > 1 ? path.replace(/\/+$/, "") : path;
  return (trimmed === "" ? "/" : trimmed).slice(0, MAX_PATH);
}

/**
 * HOST perujuk dari `document.referrer`, atau null.
 *
 * Yang disimpan HANYA host (`facebook.com`), tak pernah URL penuhnya: URL
 * perujuk kerap membawa query yang mengandung identitas, dan untuk menilai
 * iklan yang dibutuhkan cuma "datang dari mana".
 *
 * Perujuk dari HOST YANG SAMA → null: itu navigasi di dalam situs sendiri,
 * bukan sumber kunjungan. Tanpa aturan ini `kelolify.com` akan jadi "perujuk"
 * teratasnya sendiri.
 *
 * `www.` dibuang supaya `www.google.com` & `google.com` tidak jadi dua baris.
 */
export function parseReferrerHost(
  referrer: string | null | undefined,
  currentHost: string | null | undefined
): string | null {
  const raw = (referrer ?? "").trim();
  if (raw === "") return null;
  let host = "";
  try {
    host = new URL(raw).hostname.toLowerCase();
  } catch {
    return null;
  }
  if (host === "") return null;
  const strip = (h: string) => h.replace(/^www\./, "");
  const self = strip((currentHost ?? "").trim().toLowerCase().replace(/:\d+$/, ""));
  const clean = strip(host);
  if (clean === "" || (self !== "" && clean === self)) return null;
  return clean.slice(0, MAX_HOST);
}

/**
 * Baca parameter UTM dari query string.
 *
 * Dinormalkan huruf kecil supaya "Facebook" & "facebook" — dua ejaan yang
 * lazim muncul dari pengelola iklan berbeda untuk kampanye yang sama — tidak
 * dilaporkan sebagai dua sumber.
 */
export function readUtm(search: string | null | undefined): {
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
} {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search ?? "");
  } catch {
    return { utmSource: null, utmMedium: null, utmCampaign: null };
  }
  return {
    utmSource: clean(params.get("utm_source"), MAX_UTM),
    utmMedium: clean(params.get("utm_medium"), MAX_UTM),
    utmCampaign: clean(params.get("utm_campaign"), MAX_CAMPAIGN),
  };
}

/** Bahan satu kunjungan, siap dikirim ke RPC. */
export function buildVisitAttribution(input: {
  pathname?: string | null;
  search?: string | null;
  referrer?: string | null;
  currentHost?: string | null;
}): PlatformVisitAttribution {
  return {
    path: normalizeVisitPath(input.pathname),
    referrerHost: parseReferrerHost(input.referrer, input.currentHost),
    ...readUtm(input.search),
  };
}

/** True bila kunjungan ini membawa penanda kampanye apa pun. */
export function hasAttribution(a: PlatformVisitAttribution): boolean {
  return (
    a.utmSource !== null || a.utmMedium !== null || a.utmCampaign !== null
  );
}

/**
 * GERBANG KIRIM — satu-satunya penjawab "panggil RPC atau tidak".
 *
 * Dua alasan mengirim, dan yang kedua adalah inti fitur ini:
 *  1. hari ini belum tercatat di browser ini; ATAU
 *  2. URL membawa UTM — artinya orang ini BARU SAJA MENGKLIK IKLAN. Klik iklan
 *     SELALU dilaporkan, meski hari itu sudah tercatat, karena servernya
 *     memang dirancang mengisi atribusi yang masih kosong (mig 4100). Tanpa
 *     aturan ini iklan kehilangan kredit setiap kali orang yang diklik-nya
 *     kebetulan sudah pernah mampir organik hari itu — persis populasi yang
 *     paling mungkin mengklik.
 *
 * Host WAJIB apex platform. Pemanggil sudah memasang komponen ini hanya di
 * halaman jualan, jadi ini lapis kedua: `/platform` juga bisa dibuka lewat
 * domain toko, dan kunjungan di sana bukan kunjungan platform.
 */
export function shouldRecordPlatformVisit(input: {
  isPlatformHost: boolean;
  today: string;
  lastVisitedDay: string | null;
  hasUtm: boolean;
}): boolean {
  if (!input.isPlatformHost) return false;
  if (input.hasUtm) return true;
  return input.lastVisitedDay !== input.today;
}
